'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useAds } from './AdContext';
import { ADSENSE_CLIENT } from './adsenseConfig';
import { useTranslations } from 'next-intl';

export default function BannerAd({
  position = 'side', // 'side' | 'bottom' | 'panel'
  slot,
  style = {},
  showUpgradeMessage = false,
}) {
  const t = useTranslations('Auto.AdsBannerAd');
  const pathname = usePathname();
  const { shouldShowAds, hydrating } = useAds();
  const [adPushed, setAdPushed] = useState(false);
  const [adFailed, setAdFailed] = useState(false);
  const [epoch, setEpoch] = useState(0);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const adKey = `ad-${position}-${slot}-${epoch}`;
  const adInsRef = useRef<HTMLElement | null>(null);

  // Load dismissal state from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('banner-ad-dismiss');
    if (saved) {
      try {
        const { dismissed, pageCount } = JSON.parse(saved);
        setIsDismissed(dismissed);
        // Store the page count in a ref so it persists for page tracking
        if (dismissed && pageCount !== undefined) {
          localStorage.setItem('banner-ad-page-count', String(pageCount));
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
  }, []);

  // Track page navigation and reset dismissal after 5 page switches
  useEffect(() => {
    if (!isDismissed) return;

    const pageCount = parseInt(localStorage.getItem('banner-ad-page-count') || '0', 10);
    const newPageCount = pageCount + 1;

    if (newPageCount >= 5) {
      // Reset dismissal after 5 page switches
      setIsDismissed(false);
      localStorage.removeItem('banner-ad-dismiss');
      localStorage.removeItem('banner-ad-page-count');
    } else {
      // Update page count
      localStorage.setItem('banner-ad-page-count', String(newPageCount));
    }
  }, [pathname, isDismissed]);

  // Refresh every 30 seconds by incrementing epoch (resets adPushed + re-mounts ins)
  useEffect(() => {
    if (!shouldShowAds) return;
    const id = setInterval(() => setEpoch((e) => e + 1), 30_000);
    return () => clearInterval(id);
  }, [shouldShowAds]);

  // Handle banner dismiss with animation
  const handleDismiss = () => {
    setIsAnimating(true);
    setIsDismissed(true);
    localStorage.setItem('banner-ad-dismiss', JSON.stringify({ dismissed: true, pageCount: 0 }));
    localStorage.setItem('banner-ad-page-count', '0');
  };

  // Reset adPushed + adFailed when epoch advances so the push effect re-fires
  useEffect(() => {
    if (epoch > 0) {
      setAdPushed(false);
      setAdFailed(false);
    }
  }, [epoch]);

  useEffect(() => {
    if (!shouldShowAds || !slot || adPushed) return;

    let cancelled = false;
    let attempts = 0;
    let statusObserver: MutationObserver | null = null;
    let statusTimer: ReturnType<typeof setTimeout> | null = null;

    // AdSense sets data-ad-status="unfilled" on the <ins> when there is no ad
    // to serve. When that happens we collapse the component (return null) so
    // paid users / no-fill cases never show an empty gray outline.
    const watchFillStatus = (el: HTMLElement) => {
      const check = () => {
        const status = el.getAttribute('data-ad-status');
        if (status === 'unfilled') {
          setAdFailed(true);
          return true;
        }
        return false;
      };
      if (check()) return;
      statusObserver = new MutationObserver(() => {
        if (check()) statusObserver?.disconnect();
      });
      statusObserver.observe(el, { attributes: true, attributeFilter: ['data-ad-status'] });
      // Fallback: if the attribute never appears (e.g. blocked), give up the
      // gray box after a short grace period.
      statusTimer = setTimeout(() => {
        if (!el.getAttribute('data-ad-status')) setAdFailed(true);
      }, 4000);
    };

    const pushAd = () => {
      if (cancelled) return;

      try {
        const adInsElement = adInsRef.current;
        if (!adInsElement || !adInsElement.isConnected) {
          if (attempts < 10) {
            attempts += 1;
            setTimeout(pushAd, 300);
          }
          return;
        }

        if (adInsElement.getAttribute('data-adsbygoogle-status')) {
          setAdPushed(true);
          watchFillStatus(adInsElement);
          return;
        }

        if (typeof window !== 'undefined' && (window as any).adsbygoogle) {
          ((window as any).adsbygoogle = (window as any).adsbygoogle || []).push({});
          setAdPushed(true);
          watchFillStatus(adInsElement);
        } else {
          if (attempts < 10) {
            attempts += 1;
            setTimeout(pushAd, 500);
          } else {
            setAdFailed(true);
          }
        }
      } catch (e) {
        if (attempts < 10) {
          attempts += 1;
          setTimeout(pushAd, 500);
        } else {
          setAdFailed(true);
        }
      }
    };

    // Delay to ensure DOM is ready
    const timer = setTimeout(pushAd, 1000);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (statusTimer) clearTimeout(statusTimer);
      statusObserver?.disconnect();
    };
  }, [shouldShowAds, slot, adPushed, position]);

  if (hydrating || !shouldShowAds || !slot || adFailed || isDismissed) return null;

  const isBottomAd = position === 'bottom';
  const isPanelAd = position === 'panel';

  // Google AdSense supports "responsive" ad units via `data-ad-format="auto"`
  // + `data-full-width-responsive="true"`. For the bottom banner we keep a
  // fixed 728×90 on desktop but cap the rendered box to the viewport width
  // on mobile so the iframe never overflows the screen.
  return (
    <div className="flex w-full flex-col items-center gap-2" style={style}>
      {showUpgradeMessage && !isBottomAd && !isPanelAd && (
        <div className="p-2 bg-muted rounded text-center max-w-[160px]">
          <p className="text-xs text-muted-foreground leading-snug">
            Ads are annoying, but they help us pay for your servers.{' '}
            <Link href="/create" className="text-xs text-primary hover:underline">
              {t('buyingAPaidPlan')}
            </Link>{' '}
            will hide all ads.
          </p>
        </div>
      )}

      {isBottomAd ? (
        <div
          className={`relative bg-muted rounded overflow-hidden w-full max-w-[728px] h-[20px] sm:h-[30px] md:h-[60px] [&_ins]:!h-full [&_ins]:!max-h-full [&_iframe]:!h-full [&_iframe]:!max-h-full transition-all duration-300 ${
            isAnimating ? 'animate-slide-down opacity-0' : 'opacity-100'
          }`}
        >
          {/* Close button - visible only on mobile */}
          <button
            onClick={handleDismiss}
            className="absolute left-1 top-1/2 -translate-y-1/2 z-50 hover:bg-muted-foreground/20 rounded p-1 flex items-center justify-center transition-colors md:hidden text-foreground"
            aria-label={t('dismissAd')}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="4" y1="4" x2="12" y2="12" />
              <line x1="12" y1="4" x2="4" y2="12" />
            </svg>
          </button>
          <ins
            key={adKey}
            ref={adInsRef as any}
            className="adsbygoogle block w-full h-full"
            style={{ display: 'block', width: '100%', height: '100%' }}
            data-ad-client={ADSENSE_CLIENT}
            data-ad-slot={slot}
            data-ad-format="horizontal"
            data-full-width-responsive="true"
          />
        </div>
      ) : isPanelAd ? (
        <div
          className="bg-muted rounded overflow-hidden w-full h-[125px] sm:h-[250px]"
        >
          <ins
            key={adKey}
            ref={adInsRef as any}
            className="adsbygoogle block w-full h-full"
            style={{ display: 'block', width: '100%', height: '100%' }}
            data-ad-client={ADSENSE_CLIENT}
            data-ad-slot={slot}
            data-ad-format="auto"
            data-full-width-responsive="true"
          />
        </div>
      ) : (
        <div
          className="bg-muted rounded overflow-hidden"
          style={{
            width: '160px',
            height: '600px',
            maxWidth: '100%',
          }}
        >
          <ins
            key={adKey}
            ref={adInsRef as any}
            className="adsbygoogle"
            style={{
              display: 'block',
              width: '160px',
              height: '600px',
              maxWidth: '100%',
            }}
            data-ad-client={ADSENSE_CLIENT}
            data-ad-slot={slot}
          />
        </div>
      )}
    </div>
  );
}

